import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, digits, maskCep, maskPhone, isAdminRole } from "@/lib/api";
import { toast } from "sonner";

// Antes da primeira contratação: o cadastro rápido não pede endereço, então ele é completado aqui (PR B).
// Salva pela mesma rota da tela Minha empresa (PUT /settings), mantendo nome, segmento e parâmetros como estão.
const ADDR = { cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", uf: "" };

export function missingFields(f) {
  const e = f.endereco;
  const falta = [];
  if (f.razao_social.trim().length < 2) falta.push("razão social");
  if (digits(f.telefone).length < 10) falta.push("telefone");
  if (digits(e.cep).length !== 8) falta.push("CEP");
  if (!e.logradouro.trim()) falta.push("rua");
  if (!e.numero.trim()) falta.push("número");
  if (!e.bairro.trim()) falta.push("bairro");
  if (!e.cidade.trim()) falta.push("cidade");
  if (e.uf.trim().length !== 2) falta.push("UF");
  return falta;
}

export function CompleteRegistrationDialog({ open, onOpenChange, onDone }) {
  const { session, refresh } = useAuth();
  const t = session?.tenant || {};
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const e = { ...ADDR, ...(t.endereco || {}) };
    setF({ razao_social: t.razao_social || "", telefone: maskPhone(t.telefone || ""), endereco: { ...e, cep: maskCep(e.cep || "") } });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const podeEditar = isAdminRole(session?.user);
  const set = (k, mask) => (ev) => setF((x) => ({ ...x, [k]: mask ? mask(ev.target.value) : ev.target.value }));
  const setAddr = (k, mask) => (ev) => setF((x) => ({ ...x, endereco: { ...x.endereco, [k]: mask ? mask(ev.target.value) : ev.target.value } }));
  const lookupCep = async () => {
    const cep = digits(f.endereco.cep);
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`).then((x) => x.json());
      if (!r.erro) setF((x) => ({ ...x, endereco: { ...x.endereco, logradouro: x.endereco.logradouro || r.logradouro || "", bairro: x.endereco.bairro || r.bairro || "", cidade: r.localidade || x.endereco.cidade, uf: r.uf || x.endereco.uf } }));
    } catch {
      /* preenchimento manual */
    }
  };
  const save = async () => {
    const falta = missingFields(f);
    if (falta.length) return toast.error(`Preencha: ${falta.join(", ")}`);
    setBusy(true);
    try {
      const e = f.endereco;
      await api.put("/settings", {
        razao_social: f.razao_social.trim(), nome_fantasia: t.nome_fantasia, telefone: digits(f.telefone), segmento: t.segmento,
        endereco: { ...e, cep: digits(e.cep), uf: e.uf.trim().toUpperCase(), logradouro: e.logradouro.trim(), numero: e.numero.trim(),
          bairro: e.bairro.trim(), cidade: e.cidade.trim(), complemento: e.complemento.trim() },
        settings: {},
      });
      await refresh();
      toast.success("Cadastro completo. Agora é só escolher a forma de pagamento.");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="complete-registration-dialog">
        <DialogHeader>
          <DialogTitle>Complete o cadastro da empresa</DialogTitle>
          <DialogDescription>Antes da primeira contratação precisamos dos dados para a cobrança e a nota fiscal. Leva 1 minuto.</DialogDescription>
        </DialogHeader>
        {!podeEditar ? (
          <p className="text-sm" data-testid="complete-registration-no-permission">Peça ao responsável pela conta (proprietário ou administrador) para completar o cadastro em Minha empresa e contratar o plano.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={digits(t.documento).length === 11 ? "Nome completo (como no CPF)" : "Razão social"} className="sm:col-span-2"><Input value={f.razao_social} onChange={set("razao_social")} data-testid="complete-razao-input" /></Field>
            <Field label="Telefone ou WhatsApp"><Input value={f.telefone} onChange={set("telefone", maskPhone)} inputMode="tel" data-testid="complete-telefone-input" /></Field>
            <Field label="CEP" hint="Preenchimento automático"><Input value={f.endereco.cep} onChange={setAddr("cep", maskCep)} onBlur={lookupCep} inputMode="numeric" data-testid="complete-cep-input" /></Field>
            <Field label="Rua" className="sm:col-span-2"><Input value={f.endereco.logradouro} onChange={setAddr("logradouro")} data-testid="complete-logradouro-input" /></Field>
            <Field label="Número"><Input value={f.endereco.numero} onChange={setAddr("numero")} data-testid="complete-numero-input" /></Field>
            <Field label="Complemento"><Input value={f.endereco.complemento} onChange={setAddr("complemento")} data-testid="complete-complemento-input" /></Field>
            <Field label="Bairro"><Input value={f.endereco.bairro} onChange={setAddr("bairro")} data-testid="complete-bairro-input" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Cidade" className="col-span-2"><Input value={f.endereco.cidade} onChange={setAddr("cidade")} data-testid="complete-cidade-input" /></Field>
              <Field label="UF"><Input value={f.endereco.uf} maxLength={2} onChange={setAddr("uf", (v) => v.toUpperCase())} data-testid="complete-uf-input" /></Field>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="complete-registration-cancel">Agora não</Button>
          {podeEditar && <Button onClick={save} disabled={busy} data-testid="complete-registration-save">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar e continuar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
